<template>
    <Toast />
    <ConfirmDialog></ConfirmDialog>
    <div class="card">
        <div class="grid grid-cols-6 gap-2">

            <div class="col-span-6">
                <div class="p-4 mb-4 text-sm text-blue-700 bg-blue-100 rounded-lg dark:bg-blue-200 dark:text-blue-800"
                    role="alert">
                    <span class="font-medium">Empresa: {{ nombreEmpresa }}</span>
                </div>
            </div>

            <div class="col-span-6 sm:col-span-3">
                <label for="periodo" class="m-4">Periodo</label>
                <Calendar inputId="periodo" v-model="data.periodo" view="month" dateFormat="yy-mm" class="mr-4"
                    placeholder="Seleccionar periodo" />
                <Button icon="pi pi-search" class="p-button-sm" @click="datosAfiliados" title="Consultar" />
            </div>

            <div class="col-span-8 sm:col-span-3 flex gap-2">
                <Button icon="pi pi-file" label="Imprimir" class="p-button-warning p-button-sm"
                    v-if="!edicionHabilitada" title="Imprimir" :disabled="afiliados.length <= 0" @click="imprimir" />
                <Button icon="pi pi-pencil" label="Habilitar edicion" class="p-button-success p-button-sm"
                    title="Edicion" :disabled="afiliados.length <= 0 || !facturable" v-if="!edicionHabilitada"
                    @click="edicionHabilitada = true" />
                <Button icon="pi pi-user" label="Agregar" class="p-button-info p-button-sm" title="Agregar Afiliado"
                    v-if="edicionHabilitada" @click="modalAddAfiliado = true" />
                <Button icon="pi pi-folder" label="Facturar" class="p-button-secondary p-button-sm"
                    title="Generar Factura" v-if="!edicionHabilitada" :disabled="afiliados.length <= 0"
                    @click="facturarPeriodo" />
                <Button icon="pi pi-save" label="Confirmar cambios" class="p-button-success p-button-sm"
                    title="Confirmar Cambios" v-if="edicionHabilitada" @click="confirmarCambios" />
                <Button icon="pi pi-arrow-left" label="Cancelar" class="p-button-warning p-button-sm" title="Cancelar"
                    v-if="edicionHabilitada" @click="cancelarEdicion" />
            </div>

            <div class="col-span-6">
                <DataTable :key="dataKey" ref="dt" :value="filteredAfiliados" :scrollable="true" scrollHeight="620px"
                    scrollDirection="both" rowGroupMode="subheader" groupRowsBy="afiliados.cuilTitular"
                    filterDisplay="menu" showGridlines class="p-datatable-sm" editMode="row"
                    @row-edit-init="onRowEditInit" @row-edit-cancel="cancelarEdicion">

                    <template #header>
                        <div class="flex items-center flex-col md:justify-between md:flex-row">
                            <h5 class="mb-2 md:m-0 p-as-md-center">AFILIADOS</h5>
                            <div>Titulares: {{ cantidadTitulares }}</div>
                            <div>Beneficiarios: {{ cantidadBeneficiarios }}</div>
                            <span class="p-input-icon-left">
                                <i class="pi pi-filter" />
                                <InputText v-model="globalFilter" placeholder="Filtrar...Nombre, Cuil" />
                            </span>
                        </div>
                    </template>

                    <!-- grupo cuil titular -->
                    <Column field="cuilTitular" header="CUIL Titular" :style="{ width: '150px' }"
                        :class="{ 'no-border': true }">
                        <template #body="{ data }">
                            <div>
                                <div v-if="data.cuil.trim() === ''" style="background-color: aquamarine;">
                                    Grupo Familiar: {{ data.numeroAfiliado }}
                                </div>
                                <div v-else>{{ data.cuilTitular }}</div>
                            </div>
                        </template>
                    </Column>

                    <!-- grupo cuil -->
                    <Column field="cuil" header="CUIL" :style="{ width: '150px' }" :class="{ 'no-border': true }">
                        <template #body="{ data }">
                            {{ data.cuil }}
                        </template>
                    </Column>

                    <!-- grupo afiliado nombre -->
                    <Column field="nombre" header="Afiliado" :style="{ width: '200px' }" :class="{ 'no-border': true }">
                        <template #body="{ data }">
                            {{ data.nombreAfiliado }}
                        </template>
                    </Column>

                    <!-- grupo familiar -->
                    <Column field="familiar" header="Familiar" :style="{ width: '100px' }"
                        :class="{ 'no-border': true }">
                        <template #body="{ data }">
                            {{ data.tipoFamiliar }}
                        </template>
                    </Column>

                    <!-- grupo edad -->
                    <Column field="edad" header="Edad" :style="{ width: '100px' }" :class="{ 'no-border': true }">
                        <template #body="{ data }">
                            <div v-if="data.cuil.trim() !== ''">
                                {{ data.edad }}
                            </div>
                        </template>
                    </Column>

                    <!-- grupo valor cuota -->
                    <Column :rowEditor="true" field="valorCuota" header="Valor Cuota" :style="{ width: '120px' }"
                        :class="{ 'no-border': true }">
                        <template #body="slotProps">
                            <div v-if="editingRowIndex === slotProps.index">
                                <InputNumber v-model="slotProps.data.valorCuota" mode="decimal" :min="0" :max="1000000"
                                    :step="0.01" />
                            </div>
                            <div v-else>
                                {{ formatCurrency(slotProps.data.valorCuota) }}
                            </div>
                        </template>
                    </Column>

                    <!-- grupo acciones -->
                    <Column header="Acciones" :exportable="false" frozen alignFrozen="right" v-if="edicionHabilitada"
                        :style="{ width: '100px' }">
                        <template #body="slotProps">
                            <div class="flex gap-2" v-if="slotProps.data.cuil.trim() !== ''">
                                <Button icon="pi pi-check" class="custom-small-button1" title="Listo"
                                    @click="onRowEditCancel()"
                                    v-if="editingRowIndex !== null && editingRowIndex === slotProps.index"></Button>
                                <Button icon="pi pi-pencil" class="custom-small-button1" title="Editar"
                                    @click="onRowEditInit(slotProps)"></Button>
                                <Button icon="pi pi-times" class="custom-small-button2" title="Quitar"
                                    @click="eliminarAfiliado(slotProps.data)"></Button>
                            </div>
                        </template>
                    </Column>
                </DataTable>
            </div>

        </div>
    </div>
    <!-- Modal -->
    <div v-if="modalAddAfiliado" class="modal-overlay">
        <div class="modal-content" @click.stop>
            <h3>Agregar Afiliado</h3>
            <br>
            <!-- Formulario de búsqueda -->
            <div class="form-group">
                <InputText placeholder="Ingresar cuil" v-model="addAfiliado.cuilBuscar" />
                <Button icon="pi pi-search" @click="validarAfiliado" class="p-button-sm" title="Buscar" />
            </div>
            <br>
            <hr>
            <br>
            <!-- DataTable de resultados -->
            <DataTable :value="item" class="p-datatable-sm">
                <Column field="numeroAfiliado" header="Nº Afiliado" />
                <Column field="cuilTitular" header="Cuil Titular" />
                <Column field="cuil" header="Cuil" />
                <Column field="nombreAfiliado" header="Afiliado" />
                <Column field="tipoFamiliar" header="Familiar" />
                <Column field="edad" header="Edad" />
                <Column field="valorCuota" header="Valor Cuota" :body="formatCurrency" />
            </DataTable>
            <!-- Botón de cerrar -->
            <br>
            <Button label="Cerrar" title="Cerrar" class="p-button-secondary p-button-sm"
                @click="cerrarModalAddAfiliado">Cerrar</Button>
            <Button label="Agregar" title="Agregar" class="p-button-success p-button-sm" @click="agregarAfiliado"
                :disabled="item.length == 0">Agregar</Button>
        </div>
    </div>
    <div class="modal-overlay" v-if="load">
        <div class="loader"></div>
    </div>
</template>
<script>
import { ref, reactive, onMounted, computed, watch } from 'vue';
import WService from '@/plugins/ws';
import Calendar from 'primevue/calendar';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Column from 'primevue/column';
import { useRoute } from 'vue-router';
import Toast from 'primevue/toast';
import { useToast } from "primevue/usetoast";
import ConfirmDialog from 'primevue/confirmdialog';
import { useConfirm } from "primevue/useconfirm";

export default {
    components: {
        Calendar, Button, DataTable, InputText, Column, InputNumber, ConfirmDialog, Toast
    },
    props: {
        info: Object,
    },
    emits: ["closeModal"],
    setup(props, { emit }) {
        const confirm = useConfirm();
        const toast = useToast();
        const route = useRoute();
        const wService = ref(new WService());
        const data = reactive({
            periodo: '',
            idEmpresa: route.params.idEmpresa,
        });
        const load = ref(false);
        const globalFilter = ref('');
        const afiliados = ref([]);
        const nombreEmpresa = ref('');
        const cantidadAfiliados = ref(0);
        const cantidadTitulares = ref(0);
        const cantidadBeneficiarios = ref(0);
        const edicionHabilitada = ref(false);
        const inputEdicion = ref(false);
        const editable = ref(false);
        const originalAfiliados = ref([]);
        const facturable = ref(false);
        const dt = ref(null);
        const dataKey = ref(0);
        const modalAddAfiliado = ref(false);
        const addAfiliado = reactive({
            cuilBuscar: '',
        });
        const item = ref([]);

        const formatCurrency = (value) => {
            if (value)
                return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
            return '';
        };

        const save = () => {
            emit("closeModal");
        };

        const datosEmpresa = async () => {
            try {
                load.value = true;
                const info = await wService.value.getMethod(`Empresas/empresaId?id=${data.idEmpresa}`);
                if (info.length > 0) {
                    nombreEmpresa.value = info[0].nombre;
                }
            } catch (err) {
                console.log(err.message);
            } finally {
                load.value = false;
            }
        };

        const formatearPeriodo = (date) => {
            if (date instanceof Date) {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                return `${year}${month}`;
            }
            return '';
        };

        const datosAfiliados = async () => {
            if (data.periodo === '') {
                toast.add({
                    severity: 'error',
                    detail: `Debe seleccionar un periodo.`,
                    life: 10000
                });
                return false;
            }
            data.periodo = formatearPeriodo(data.periodo);
            try {
                load.value = true;
                const info = await wService.value.postMethod('Empresas/InformeAfiliados', {
                    empresaId: data.idEmpresa,
                    periodo: data.periodo
                });
                if (info[0].generado == false || info[0].generado == null) {
                    facturable.value = true;
                }
                else {
                    facturable.value = false;
                }
                originalAfiliados.value = JSON.parse(JSON.stringify(info));
                afiliados.value = info;
                cantidadAfiliados.value = afiliados.value.filter(afiliado => afiliado.tipoFamiliar.trim !== "").length;
                cantidadTitulares.value = afiliados.value.filter(afiliado => afiliado.tipoFamiliar === "Titular").length;
                cantidadBeneficiarios.value = afiliados.value.filter(afiliado => afiliado.tipoFamiliar !== "Titular" && afiliado.tipoFamiliar.trim() !== "").length;

            } catch (err) {
                console.log(err.message);
            } finally {
                load.value = false;
            }
        };

        var filteredAfiliados = computed(() => {
            const filter = globalFilter.value ? globalFilter.value.toLowerCase() : '';
            return afiliados.value.filter(afiliado =>
                afiliado.nombreAfiliado?.toLowerCase().includes(filter) ||
                afiliado.cuil?.toLowerCase().includes(filter)
            );
        });

        const imprimir = async () => {
            const filas = afiliados.value.filter(r => r.cuil.trim() !== "");
            let impresion = "";
            for (let i = 0; i < filas.length; i++) {
                const row = filas[i];
                if (row.cuil !== "" && !row.anulado) {
                    impresion += row.idAfiliado + "$" + parseFloat(row.valorCuota).toFixed(2) + "-";
                }
            }

            const payload = {
                Filas: impresion,
                EmpresaID: data.idEmpresa,
                Periodo: data.periodo
            };

            try {
                load.value = true;
                const response = await wService.value.postMethod(
                    'Empresas/imprimirPanel',
                    payload,
                    { responseType: 'text' }
                );

                const binaryString = atob(response);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }

                const pdfBlob = new Blob([bytes], { type: 'application/pdf' });
                const pdfUrl = window.URL.createObjectURL(pdfBlob);

                // Crear y activar el enlace de descarga
                const a = document.createElement('a');
                a.href = pdfUrl;
                a.download = 'reporte.pdf';
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(pdfUrl);

            } catch (error) {
                console.error('Error al intentar imprimir:', error);
                console.error('Detalles del error:', error.response?.data);
            }
            finally {
                load.value = false;
            }
        };

        const facturarPeriodo = async () => {
            if (facturable.value === false) {
                toast.add({
                    severity: 'warn',
                    summary: 'Facturar',
                    detail: `Este periodo ya ha sido facturado.`,
                    life: 10000
                });
                return false;
            }
            confirm.require({
                message: `¿Seguro que desea facturar este periodo?`,
                header: 'Facturar',
                icon: 'pi pi-exclamation-triangle',
                accept: async () => {
                    load.value = true; // Mostrar loader después de aceptar
                    facturable.value = false; // Para deshabilitar el botón

                    const filas = afiliados.value.filter(r => r.cuil.trim() !== "");
                    let impresion = "";
                    for (let i = 0; i < filas.length; i++) {
                        const row = filas[i];
                        if (row.cuil !== "" && !row.anulado) {
                            impresion += row.idAfiliado + "$" + parseFloat(row.valorCuota).toFixed(2) + "-";
                        }
                    }

                    const payload = {
                        Filas: impresion,
                        EmpresaID: data.idEmpresa,
                        Periodo: data.periodo
                    };

                    try {
                        const response = await wService.value.postMethod('Empresas/generarFactura', payload);

                        if (response === 'ok') {
                            toast.add({
                                severity: 'success',
                                summary: 'Facturar',
                                detail: `Facturación exitosa.`,
                                life: 10000
                            });
                            window.location.reload(); // Considerar alternativas a recargar la página
                        } else {
                            facturable.value = true; // Habilitar el botón en caso de error
                            toast.add({
                                severity: 'error',
                                summary: 'Facturar',
                                detail: response.data.message || "Hubo un problema al generar la factura.",
                                life: 10000
                            });
                        }
                    } catch (error) {
                        facturable.value = true; // Habilitar el botón en caso de error
                        console.error('Error al intentar generar factura:', error);
                    } finally {
                        load.value = false; // Ocultar loader
                    }
                }
            });
            return false;
        };



        //quitar registro
        const eliminarAfiliado = (afiliado) => {
            const index = afiliados.value.findIndex(a => a.idAfiliado === afiliado.idAfiliado);
            if (index !== -1) {
                const afiliadoCuil = afiliado.cuil;
                const afiliadoNombre = afiliado.nombreAfiliado.trim();
                confirm.require({
                    message: `¿Está seguro que desea quitar al afiliado: ${afiliadoNombre}-${afiliadoCuil}?`,
                    header: 'Quitar',
                    icon: 'pi pi-exclamation-triangle',
                    accept: () => {
                        afiliados.value.splice(index, 1);
                    }
                });
                return false;
            }
        };

        //edicion
        const editingRowIndex = ref(null);

        const onRowEditInit = (event) => {
            editingRowIndex.value = event.index;
        };

        const onRowEditCancel = () => {
            editingRowIndex.value = null;

        };

        const cancelarEdicion = () => {
            edicionHabilitada.value = false;
            editingRowIndex.value = null; // Desactivar la edición en fila
            afiliados.value = JSON.parse(JSON.stringify(originalAfiliados.value)); //Restaurar copia de los datos sin editar
            dataKey.value += 1; // Forzar re-renderización de la tabla
        };



        //confirmarCambios
        const confirmarCambios = async () => {
            confirm.require({
                message: `¿Seguro que desea guardar los cambios realizados?`,
                header: 'Confirmar cambios',
                icon: 'pi pi-exclamation-triangle',
                accept: () => {
                    const filas = afiliados.value.filter(r => r.cuil.trim() !== "");
                    let guardar = "";
                    for (let i = 0; i < filas.length; i++) {
                        const row = filas[i];
                        if (row.cuil !== "" && !row.anulado) {
                            guardar += row.idAfiliado + "$" + parseFloat(row.valorCuota).toFixed(2) + "-";
                        }
                    }

                    const payload = {
                        Filas: guardar,
                        EmpresaID: data.idEmpresa,
                        Periodo: data.periodo
                    };

                    try {
                        load.value = true;
                        const response = wService.value.postMethod(
                            'Empresas/confirmarCambios',
                            payload,
                            { responseType: 'text' }
                        );
                        if (response) {
                            toast.add({
                                severity: 'success',
                                detail: 'Cambios guardados correctamente.',
                                life: 10000
                            });
                            edicionHabilitada.value = false;
                            dataKey.value += 1;
                            window.location.reload();
                        }

                    } catch (error) {
                        console.error('Error al intentar guardar:', error);
                    } finally {
                        load.value = false;
                    }
                }
            });
            return false;
        }
        const cerrarModalAddAfiliado = () => {
            modalAddAfiliado.value = false;
            item.value = [];
            addAfiliado.cuilBuscar = '';
        };

        const validarAfiliado = async () => {
            if (addAfiliado.cuilBuscar.length !== 11 || !/^\d{11}$/.test(addAfiliado.cuilBuscar)) {
                toast.add({
                    severity: 'error',
                    detail: 'Cuil inválido. Debe ser un número de 11 dígitos.',
                    life: 10000
                });
                return false;
            }
            const datos = {
                Periodo: data.periodo,
                Cuil: addAfiliado.cuilBuscar
            };

            try {
                const response = await wService.value.postMethod(
                    'Empresas/buscarCuil',
                    datos,
                );
                if (!response) {
                    console.error("Error al obtener datos");
                } else {
                    item.value = response;
                }
            } catch (error) {
                console.error('Error al validar afiliado:', error);
            }

        };
        const agregarAfiliado = () => {
            const cuilBuscar = addAfiliado.cuilBuscar.trim();
            const existeAfiliado = afiliados.value.some(afiliado => afiliado.cuil === cuilBuscar);

            if (existeAfiliado) {
                toast.add({
                    severity: 'error',
                    detail: 'El afiliado ya está en la lista.',
                    life: 10000
                });
            } else {
                const afiliadoParaAgregar = item.value.find(afiliado => afiliado.cuil === cuilBuscar);
                if (afiliadoParaAgregar) {
                    afiliados.value.push(afiliadoParaAgregar);
                    dataKey.value += 1; // Forzar re-renderización de la tabla
                    toast.add({
                        severity: 'success',
                        detail: 'Afiliado agregado correctamente.',
                        life: 10000
                    });
                    cerrarModalAddAfiliado();
                } else {
                    toast.add({
                        severity: 'error',
                        detail: 'No se encontró el afiliado.',
                        life: 10000
                    });
                }
            }
        };

        onMounted(() => {
            datosEmpresa();
        });

        return {
            toast, confirm, route, data, globalFilter, afiliados, load, nombreEmpresa, formatCurrency, save, formatearPeriodo, datosAfiliados, onRowEditInit, onRowEditCancel, facturable,
            editable, filteredAfiliados, cantidadTitulares, cantidadBeneficiarios, cantidadAfiliados, edicionHabilitada, editingRowIndex, inputEdicion, imprimir, facturarPeriodo,
            eliminarAfiliado, cancelarEdicion, originalAfiliados, dt, dataKey, confirmarCambios, modalAddAfiliado, validarAfiliado, addAfiliado, item, agregarAfiliado, cerrarModalAddAfiliado
        }
    }
}
</script>
<style scoped>
.custom-small-button1 {
    padding: 0.25rem 0.25rem;
    line-height: 1;
}

.custom-small-button2 {
    padding: 0.25rem 0.25rem;
    line-height: 1;
}

.no-border {
    border: none !important;
    border-width: 0 !important;
}

.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 999;
}

.modal-content {
    background: white;
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    max-width: 80%;
    width: 800px;
}

.loader {
    width: 48px;
    height: 48px;
    border: 5px solid black;
    border-bottom-color: transparent;
    border-radius: 50%;
    display: inline-block;
    box-sizing: border-box;
    animation: rotation 1s linear infinite;
}

@keyframes rotation {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}
</style>